import { useState } from 'react';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import type { WorkItem, Sprint } from '../../types';

interface SprintRolloverModalProps {
  incompleteItems: WorkItem[];
  fromSprint: Sprint;
  toSprint: Sprint;
  onRollover: (workItemIds: string[]) => void;
  onCancel: () => void;
}

export default function SprintRolloverModal({
  incompleteItems,
  fromSprint,
  toSprint,
  onRollover,
  onCancel,
}: SprintRolloverModalProps) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(incompleteItems.map((i) => i._id))
  );

  const toggleItem = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === incompleteItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(incompleteItems.map((i) => i._id)));
    }
  };

  const typeColors: Record<string, string> = {
    epic: 'bg-purple-500',
    feature: 'bg-blue-500',
    story: 'bg-emerald-500',
    task: 'bg-amber-500',
    bug: 'bg-red-500',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Sprint Rollover
            </h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </div>

        {/* Description */}
        <div className="px-5 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <strong>{fromSprint.name}</strong> has been completed.{' '}
            {incompleteItems.length} incomplete item{incompleteItems.length !== 1 ? 's' : ''} can be rolled over to{' '}
            <strong>{toSprint.name}</strong>.
          </p>
        </div>

        {/* Item list */}
        <div className="px-5 py-3 max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.size === incompleteItems.length}
                onChange={toggleAll}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Select All
            </label>
            <span className="text-xs text-gray-400">{selected.size} selected</span>
          </div>

          <div className="space-y-1">
            {incompleteItems.map((item) => (
              <label
                key={item._id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(item._id)}
                  onChange={() => toggleItem(item._id)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <span className={`w-2 h-2 rounded-full shrink-0 ${typeColors[item.workItemType] || 'bg-gray-400'}`} />
                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                  {item.displayId}
                </span>
                <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                  {item.title}
                </span>
                <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 shrink-0">
                  {item.status}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onRollover(Array.from(selected))}
            disabled={selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowRight size={14} />
            Roll Over {selected.size} Item{selected.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
